import { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn } from 'core/userContext';
import { AlertsHistoryBadge } from 'modules/campaigns/lib/components/AlertsHistoryBadge';
import { CAMPAIGN_METADATA_ICON_SIZE } from 'modules/campaigns/lib/constants';
import {
  CAMPAIGN_TYPE_CONTENT_MAPPER,
  MESSAGE_MEDIUM_CONTENT_MAPPER,
  getCampaignStateBadgeVariant,
  pluralizeWord,
} from 'modules/campaigns/lib/utils';
import {
  Campaign,
  CampaignState,
  CampaignType,
  Experiment,
  MessageMedium,
} from 'modules/domains/campaign/types';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';
import { Placement } from 'modules/placements/types';
import { Badge } from 'ui/badge';
import { ExperimentFlask } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { DATE_FORMAT_WITH_COMMA_HOUR_MINS, displayDate } from 'utils/date';

import noop from 'utils/noop';
import { GraphQLTemplateAndContent } from '../../../editor/types';
import { getLatestExperiment } from '../../utils/experiment';
import {
  CAMPAIGN_HEADER_DETAILS_CAMPAIGN_ID_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_CAMPAIGN_TYPE_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_CREATED_ON_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_EXPERIMENTS_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_LOCALES_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_MESSAGE_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_PLACEMENT_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_TEMPLATE_ID_TEST_ID,
  CAMPAIGN_HEADER_DETAILS_TEST_ID,
} from './CampaignHeaderDetails.constants';
import {
  CampaignDetailButtonLink,
  CampaignDetailsBlock,
  CampaignDetailsContainer,
} from './CampaignHeaderDetails.styled';

export const getExperimentDisplayText = (experiments: Experiment) => {
  const variationsCount = experiments?.variations?.length;
  return `${variationsCount} Experiment ${pluralizeWord('variant', variationsCount)}`;
};

export type CampaignDetailProps = {
  id: number;
  campaignPlacement?: Placement;
  campaignState: CampaignState;
  campaignTypeDefinition: CampaignType;
  createdAt: string;
  experiments?: Experiment[];
  messageMedium: MessageMedium;
};

export type TemplateDetailProps = Pick<GraphQLTemplateAndContent, 'name'> &
  Pick<Campaign, 'templateId'>;

type CampaignHeaderDetailsProps = {
  campaignDetails: CampaignDetailProps;
  children: ReactNode;
  templateDetails?: TemplateDetailProps;
  templateLocales?: string[];
  /**
   * This is used to remove the created at date
   * as we don't display it on the header for campaigns */
  withoutCreatedAt?: boolean;
  /**  This is used to display the alerts history button */
  hasRecentTriggeredAlerts?: boolean;
  onViewAlerts?: () => void;
  isDefaultWhatsAppTemplate?: boolean;
};

/**
 * This header is currently shared between
 *
 * a) the campaign editor/summary view
 * b) campaign analytics
 *
 * These pages are found at the following paths:
 * `/campaigns/[ID]`
 * `/campaigns/[ID]?view=Summary`
 * `/analytics/campaignPerformance?campaignId=[ID]`
 *
 */
export const CampaignHeaderDetails = ({
  campaignDetails,
  children,
  templateDetails,
  templateLocales = [],
  withoutCreatedAt = false,
  hasRecentTriggeredAlerts = false,
  onViewAlerts = noop,
  isDefaultWhatsAppTemplate = false,
}: CampaignHeaderDetailsProps) => {
  const {
    id,
    campaignPlacement,
    campaignState,
    campaignTypeDefinition,
    createdAt,
    experiments = [],
    messageMedium,
  } = campaignDetails;

  const { Icon: MessageIcon, displayName: messageDisplayName } =
    MESSAGE_MEDIUM_CONTENT_MAPPER[messageMedium as MessageMedium] || {};
  const { Icon: CampaignTypeIcon, displayName: campaignDisplayName } =
    CAMPAIGN_TYPE_CONTENT_MAPPER[campaignTypeDefinition as CampaignType] || {};

  const badgeVariant = getCampaignStateBadgeVariant(campaignState as CampaignState);
  const experiment = useMemo(() => getLatestExperiment(experiments), [experiments]);
  const { getExperimentLinkAttributes } = useExperimentTakeover();

  const attributes = getExperimentLinkAttributes({
    source: ExperimentTakeoverSource.CAMPAIGN_EDITOR,
    experiment,
  });

  const isCampaignPerformanceAlertsHistoryEnabled = useSelector(
    selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  );

  return (
    <>
      <CampaignDetailsContainer data-test={CAMPAIGN_HEADER_DETAILS_TEST_ID}>
        <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_MESSAGE_TEST_ID}>
          <MessageIcon {...CAMPAIGN_METADATA_ICON_SIZE} /> {messageDisplayName}
        </CampaignDetailsBlock>
        {campaignPlacement && (
          <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_PLACEMENT_TEST_ID}>
            {campaignPlacement.placementName}
          </CampaignDetailsBlock>
        )}
        <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_CAMPAIGN_TYPE_TEST_ID}>
          <CampaignTypeIcon {...CAMPAIGN_METADATA_ICON_SIZE} /> {campaignDisplayName}
        </CampaignDetailsBlock>
        <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_CAMPAIGN_ID_TEST_ID}>
          Campaign ID:&nbsp;{id}
        </CampaignDetailsBlock>
        {!withoutCreatedAt && (
          <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_CREATED_ON_TEST_ID}>
            Created {displayDate(createdAt, DATE_FORMAT_WITH_COMMA_HOUR_MINS)}
          </CampaignDetailsBlock>
        )}
        {templateDetails && !isDefaultWhatsAppTemplate && (
          <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_TEMPLATE_ID_TEST_ID}>
            <Tooltip isUnderlined tooltipContent={templateDetails.name}>
              Template ID:&nbsp;{templateDetails.templateId}
            </Tooltip>
          </CampaignDetailsBlock>
        )}
        {!isDefaultWhatsAppTemplate && templateLocales.length > 0 && (
          <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_LOCALES_TEST_ID}>
            <Tooltip
              isUnderlined
              tooltipContent={templateLocales?.map((locale, i) => {
                const comma = i === templateLocales.length - 1 ? '' : ',';
                return `${locale}${comma}\n`;
              })}
            >
              <>{`${templateLocales?.length} locale${templateLocales.length > 1 ? 's' : ''}`}</>
            </Tooltip>
          </CampaignDetailsBlock>
        )}
        {experiment && (
          <CampaignDetailsBlock data-test={CAMPAIGN_HEADER_DETAILS_EXPERIMENTS_TEST_ID}>
            <ExperimentFlask />
            <CampaignDetailButtonLink {...attributes}>
              {getExperimentDisplayText(experiment)}
            </CampaignDetailButtonLink>
          </CampaignDetailsBlock>
        )}

        <Badge variant={badgeVariant}>{campaignState}</Badge>
        {isCampaignPerformanceAlertsHistoryEnabled && hasRecentTriggeredAlerts && (
          <AlertsHistoryBadge onClick={onViewAlerts} />
        )}
      </CampaignDetailsContainer>
      {children}
    </>
  );
};
